import React, { useCallback, useEffect, useState } from "react";
import { Button, Popconfirm, Space, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  useDeleteSimulationScenarioMutation,
  useLazyListSimulationScenariosQuery,
} from "../../redux/simulation/simulationAPI";
import { AdapterProtocol } from "../../flatbuffers/generated/archive/adapter-protocol";
import type { SimulationScenarioT } from "../../flatbuffers/generated/api/simulation-scenario";
import { formatUuidT } from "../../flatbuffers/display";
import SimulationScenarioBuilder from "./SimulationScenarioBuilder";

const text = (value: string | Uint8Array | null | undefined): string => String(value ?? "");

/** A copy of `scenario` to save as a new one: no id, its name marked as a copy. */
export function duplicateScenario(
  scenario: SimulationScenarioT,
  copySuffix: string,
): SimulationScenarioT {
  const copy = scenario.clone();
  copy.id = null;
  copy.createdAt = null;
  copy.updatedAt = null;
  copy.updatedBy = null;
  copy.name = `${text(scenario.name)} ${copySuffix}`;
  return copy;
}

interface SimulationScenariosTabProps {
  /** The organization whose scenarios are shown, as a dashed UUID string. */
  orgId: string;
}

/** The organization's saved scenarios, and the builder to make or change one. */
function SimulationScenariosTab({ orgId }: SimulationScenariosTabProps) {
  const [t] = useTranslation("global");
  const [triggerList, { data: scenarios = [], isFetching }] = useLazyListSimulationScenariosQuery();
  const [deleteScenario] = useDeleteSimulationScenarioMutation();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<SimulationScenarioT | null>(null);

  const refresh = useCallback(async () => {
    const token = await GETJwtToken();
    triggerList({ orgId, token });
  }, [orgId, triggerList]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openBuilder = (scenario: SimulationScenarioT | null) => {
    setEditing(scenario);
    setBuilderOpen(true);
  };

  const onDelete = async (scenario: SimulationScenarioT) => {
    const token = await GETJwtToken();
    try {
      await deleteScenario({ id: formatUuidT(scenario.id), token }).unwrap();
      refresh();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.deleteFailed"));
    }
  };

  const columns: ColumnsType<SimulationScenarioT> = [
    { title: t("simulation.name"), key: "name", render: (_, scenario) => text(scenario.name) },
    {
      title: t("simulation.sandbox"),
      key: "sandbox",
      render: (_, scenario) => <code>{text(scenario.sandbox)}</code>,
    },
    {
      title: t("simulation.banks"),
      key: "banks",
      render: (_, scenario) => {
        const enabled = scenario.banks.filter((bank) => bank.enabled);
        const protocols = [...new Set(enabled.map((bank) => bank.protocol))];
        return (
          <Space size="small" wrap>
            {t("simulation.bankCount", { count: enabled.length })}
            {protocols.map((protocol) => (
              <Tag key={protocol}>{AdapterProtocol[protocol]}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t("simulation.updated"),
      key: "updated",
      render: (_, scenario) => `${text(scenario.updatedBy)} · ${text(scenario.updatedAt)}`,
    },
    {
      title: t("simulation.actions"),
      key: "actions",
      render: (_, scenario) => (
        <Space>
          <Button size="small" onClick={() => openBuilder(scenario)}>
            {t("simulation.edit")}
          </Button>
          <Button
            size="small"
            onClick={() => openBuilder(duplicateScenario(scenario, t("simulation.copySuffix")))}
          >
            {t("simulation.duplicate")}
          </Button>
          <Popconfirm
            title={t("simulation.deleteScenarioConfirm", { name: text(scenario.name) })}
            onConfirm={() => onDelete(scenario)}
          >
            <Button size="small" danger>
              {t("simulation.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" className="generalStyles__width100">
      <Button type="primary" onClick={() => openBuilder(null)}>
        {t("simulation.newScenario")}
      </Button>
      <Table
        size="small"
        rowKey={(scenario) => formatUuidT(scenario.id)}
        columns={columns}
        dataSource={scenarios}
        loading={isFetching}
        pagination={false}
      />
      <SimulationScenarioBuilder
        orgId={orgId}
        initial={editing}
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          refresh();
        }}
      />
    </Space>
  );
}

export default SimulationScenariosTab;
